import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Header, Modal, Form, Message, Icon, Input, Pagination } from 'semantic-ui-react';
import AdminLayout from '../../components/layout/AdminLayout';
import { adminOils,oils } from '../../services/api';

function Oils() {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedOil, setSelectedOil] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  // Export oils as CSV
  const handleExportCsv = async () => {
    try {
      const response = await adminOils.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'oils_export.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      setError('Failed to export oils: ' + (err.response?.data?.error || err.message));
    }
  };

  // Import oils from CSV
  const handleImportCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportResult(null);
    setImportError('');
    try {
      const response = await adminOils.importCsv(file);
      setImportResult(response.data);
      queryClient.invalidateQueries(['admin-oils']);
    } catch (err) {
      setImportError(err.response?.data?.error || err.message);
    }
  };

  const { data: oilsList, isLoading } = useQuery({
    queryKey: ['admin-oils', search, currentPage],
    queryFn: () => oils.list({ search, page: currentPage }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminOils.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-oils']);
      setCreateModalOpen(false);
    },
    onError: (err) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => adminOils.update(selectedOil.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-oils']);
      setEditModalOpen(false);
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminOils.delete(id),
    onSuccess: () => queryClient.invalidateQueries(['admin-oils']),
    onError: (err) => setError(err.message),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const breakdown = {
      lauric: Number(formData.get('lauric')) || 0,
      myristic: Number(formData.get('myristic')) || 0,
      palmitic: Number(formData.get('palmitic')) || 0,
      stearic: Number(formData.get('stearic')) || 0,
      ricinoleic: Number(formData.get('ricinoleic')) || 0,
      oleic: Number(formData.get('oleic')) || 0,
      linoleic: Number(formData.get('linoleic')) || 0,
      linolenic: Number(formData.get('linolenic')) || 0,
    };
    
    const properties = {
      bubbly: Number(formData.get('bubbly')) || 0,
      cleansing: Number(formData.get('cleansing')) || 0,
      condition: Number(formData.get('condition')) || 0,
      hardness: Number(formData.get('hardness')) || 0,
      longevity: Number(formData.get('longevity')) || 0,
      stable: Number(formData.get('stable')) || 0,
    };

    createMutation.mutate({
      name: formData.get('name'),
      sap: Number(formData.get('sap')),
      iodine: Number(formData.get('iodine')),
      ins: Number(formData.get('ins')),
      breakdown,
      properties,
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const breakdown = {
      lauric: Number(formData.get('lauric')) || 0,
      myristic: Number(formData.get('myristic')) || 0,
      palmitic: Number(formData.get('palmitic')) || 0,
      stearic: Number(formData.get('stearic')) || 0,
      ricinoleic: Number(formData.get('ricinoleic')) || 0,
      oleic: Number(formData.get('oleic')) || 0,
      linoleic: Number(formData.get('linoleic')) || 0,
      linolenic: Number(formData.get('linolenic')) || 0,
    };
    
    const properties = {
      bubbly: Number(formData.get('bubbly')) || 0,
      cleansing: Number(formData.get('cleansing')) || 0,
      condition: Number(formData.get('condition')) || 0,
      hardness: Number(formData.get('hardness')) || 0,
      longevity: Number(formData.get('longevity')) || 0,
      stable: Number(formData.get('stable')) || 0,
    };

    updateMutation.mutate({
      name: formData.get('name'),
      sap: Number(formData.get('sap')),
      iodine: Number(formData.get('iodine')),
      ins: Number(formData.get('ins')),
      breakdown,
      properties,
    });
  };

  const handleDelete = (oil) => {
    if (window.confirm(`Are you sure you want to delete ${oil.name}?`)) {
      deleteMutation.mutate(oil.id);
    }
  };

  const handlePageChange = (_, { activePage }) => {
    setCurrentPage(activePage);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Miakara Soap Recipes - Oil Management</title>
      </Helmet>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Header as="h1">Oil Management</Header>
        <div>
          <Button onClick={handleExportCsv} style={{ marginRight: 8 }}>
            Export Oils (CSV)
          </Button>
          <Button as="label" htmlFor="import-oils-csv" style={{ marginRight: 8 }}>
            Import Oils (CSV)
            <input
              id="import-oils-csv"
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleImportCsv}
            />
          </Button>
          <Button primary onClick={() => setCreateModalOpen(true)}>
            Create Oil
          </Button>
        </div>
      </div>

      <Input
        icon="search"
        placeholder="Search oils..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 16 }}
        fluid
      />

      {error && <Message negative content={error} />}
      {importError && <Message negative content={importError} />}
      {importResult && (
        <Message positive>
          <Message.Header>Import Complete</Message.Header>
          <p>Oils updated: {importResult.updated?.length || 0}</p>
          {importResult.errors?.length > 0 && (
            <>
              <p>Errors:</p>
              <ul>
                {importResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </>
          )}
        </Message>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>SAP</Table.HeaderCell>
            <Table.HeaderCell>Iodine</Table.HeaderCell>
            <Table.HeaderCell>INS</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {oilsList?.data?.data?.map(oil => (
            <Table.Row key={oil.id}>
              <Table.Cell>{oil.name}</Table.Cell>
              <Table.Cell>{oil.sap}</Table.Cell>
              <Table.Cell>{oil.iodine}</Table.Cell>
              <Table.Cell>{oil.ins}</Table.Cell>
              <Table.Cell>
                <Button.Group size="tiny">
                  <Button
                    icon
                    onClick={() => {
                      setSelectedOil(oil);
                      setEditModalOpen(true);
                    }}
                  >
                    <Icon name="edit" />
                  </Button>
                  <Button icon negative onClick={() => handleDelete(oil)}>
                    <Icon name="trash" />
                  </Button>
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {oilsList?.data?.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination
            activePage={currentPage}
            totalPages={oilsList.data.last_page}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
      >
        <Modal.Header>Create Oil</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleCreate}>
            <Form.Input
              required
              label="Name"
              name="name"
              placeholder="Enter oil name"
            />
            <Form.Group widths="equal">
              <Form.Input
                required
                type="number"
                step="0.001"
                label="SAP Value"
                name="sap"
                placeholder="Enter SAP value"
              />
              <Form.Input
                required
                type="number"
                label="Iodine Value"
                name="iodine"
                placeholder="Enter iodine value"
              />
              <Form.Input
                required
                type="number"
                label="INS Value"
                name="ins"
                placeholder="Enter INS value"
              />
            </Form.Group>

            <Header as="h4">Fatty Acid Breakdown</Header>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Lauric"
                name="lauric"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Myristic"
                name="myristic"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Palmitic"
                name="palmitic"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Stearic"
                name="stearic"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Ricinoleic"
                name="ricinoleic"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Oleic"
                name="oleic"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Linoleic"
                name="linoleic"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Linolenic"
                name="linolenic"
                placeholder="0.00"
              />
            </Form.Group>
            
            <Header as="h4">Oil Properties</Header>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Bubbly"
                name="bubbly"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Cleansing"
                name="cleansing"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Condition"
                name="condition"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Hardness"
                name="hardness"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Longevity"
                name="longevity"
                placeholder="0.00"
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Stable"
                name="stable"
                placeholder="0.00"
              />
            </Form.Group>
            <Button type="submit" primary>Create</Button>
          </Form>
        </Modal.Content>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
      >
        <Modal.Header>Edit Oil</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleUpdate}>
            <Form.Input
              required
              label="Name"
              name="name"
              placeholder="Enter oil name"
              defaultValue={selectedOil?.name}
            />
            <Form.Group widths="equal">
              <Form.Input
                required
                type="number"
                step="0.001"
                label="SAP Value"
                name="sap"
                placeholder="Enter SAP value"
                defaultValue={selectedOil?.sap}
              />
              <Form.Input
                required
                type="number"
                label="Iodine Value"
                name="iodine"
                placeholder="Enter iodine value"
                defaultValue={selectedOil?.iodine}
              />
              <Form.Input
                required
                type="number"
                label="INS Value"
                name="ins"
                placeholder="Enter INS value"
                defaultValue={selectedOil?.ins}
              />
            </Form.Group>

            <Header as="h4">Fatty Acid Breakdown</Header>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Lauric"
                name="lauric"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.lauric}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Myristic"
                name="myristic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.myristic}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Palmitic"
                name="palmitic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.palmitic}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Stearic"
                name="stearic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.stearic}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Ricinoleic"
                name="ricinoleic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.ricinoleic}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Oleic"
                name="oleic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.oleic}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Linoleic"
                name="linoleic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.linoleic}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Linolenic"
                name="linolenic"
                placeholder="0.00"
                defaultValue={selectedOil?.breakdown?.linolenic}
              />
            </Form.Group>
            
            <Header as="h4">Oil Properties</Header>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Bubbly"
                name="bubbly"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.bubbly}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Cleansing"
                name="cleansing"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.cleansing}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Condition"
                name="condition"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.condition}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Hardness"
                name="hardness"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.hardness}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                step="0.01"
                label="Longevity"
                name="longevity"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.longevity}
              />
              <Form.Input
                type="number"
                step="0.01"
                label="Stable"
                name="stable"
                placeholder="0.00"
                defaultValue={selectedOil?.properties?.stable}
              />
            </Form.Group>
            <Button type="submit" primary>Update</Button>
          </Form>
        </Modal.Content>
      </Modal>
    </AdminLayout>
  );
}

export default Oils; 